import { EventContextProvider } from "@codat/analytics";
import { Col, Divider, Grid, Row } from "@codat/orchard-ui";
import classNames from "classnames";
import { usePageWidthViewContext } from "contexts/PageWidthViewContext";
import { useProducts } from "contexts/ProductsContext";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";

import { createNavPermissionMap } from "components/NavContainer/utils";
import Sidebar, { SidebarGroup } from "components/Sidebar/Sidebar";

import getSidebarGroups from "./getSidebarGroups";
import styles from "./Page.module.css";

export interface PageProps {
  header?: React.ReactElement;
  sidebarClassName?: string;
  sidebarGroupsOverride?: SidebarGroup[];
  gridClassName?: string;
  sidebarAndContentClassName?: string;
  sidebarLoading?: boolean;
}

const Page: React.FC<React.PropsWithChildren<PageProps>> = ({
  header,
  sidebarClassName,
  sidebarGroupsOverride,
  gridClassName,
  sidebarAndContentClassName,
  sidebarLoading,
  ...props
}) => {
  const { featureStatus } = useFeatureStatus();
  const location = useLocation();
  const { userState } = useUserState();

  const { fullWidthView } = usePageWidthViewContext();

  const navPermissionMap = createNavPermissionMap(
    userState ? userState.userPermissions : [],
    userState?.userRole
  );

  const { products } = useProducts();

  const sidebarGroups = getSidebarGroups(
    location.pathname,
    navPermissionMap,
    products,
    featureStatus
  );

  return (
    <div className={styles.page}>
      {header && (
        <div
          className={classNames(styles.header, {
            [styles.fullWidth]: fullWidthView,
          })}
        >
          {header}
          <Divider />
        </div>
      )}
      <SidebarAndContent
        sidebarClassName={sidebarClassName}
        gridClassName={gridClassName}
        sidebarGroups={sidebarGroupsOverride || sidebarGroups}
        sidebarAndContentClassName={sidebarAndContentClassName}
        isLoading={!userState || !featureStatus || !!sidebarLoading}
        {...props}
      />
    </div>
  );
};

export default Page;

interface SidebarAndContentProps {
  sidebarClassName?: string;
  gridClassName?: string;
  sidebarGroups: SidebarGroup[] | undefined;
  sidebarAndContentClassName?: string;
  isLoading: boolean;
}

export const SidebarAndContent: React.FC<
  PropsWithChildren<SidebarAndContentProps>
> = ({
  children,
  sidebarClassName,
  gridClassName,
  sidebarGroups,
  sidebarAndContentClassName,
  isLoading,
  ...props
}) => {
  const { fullWidthView } = usePageWidthViewContext();

  return (
    <>
      <div
        className={classNames(
          styles.sidebarAndContent,
          sidebarAndContentClassName,
          {
            [styles.withSidebar]: !!sidebarGroups,
          }
        )}
        data-testid="page-wrapper"
      >
        {sidebarGroups && (
          <EventContextProvider name="Sidebar">
            <Sidebar
              className={classNames(styles.sidebar, sidebarClassName)}
              sidebarGroups={sidebarGroups}
              isLoading={isLoading}
            />
          </EventContextProvider>
        )}
        <Grid
          {...props}
          className={classNames(
            styles.grid,
            gridClassName,
            fullWidthView && styles.fullWidth
          )}
          data-testid="content-grid"
          fullWidth={fullWidthView}
        >
          <Row>
            <Col size={12}>{children}</Col>
          </Row>
        </Grid>
      </div>
    </>
  );
};
